import { jsPDF } from 'jspdf';

// Branding configuration interface
export interface PdfBrandingConfig {
  logoHeight: number;       // Logo height in mm (width auto-calculated from ratio)
  headerTitle: string;      // Text next to logo (e.g., "Kompakt")
  slogan: string;           // Footer slogan
  websiteUrl: string;       // Footer URL
}

// Default branding config
export const defaultBrandingConfig: PdfBrandingConfig = {
  logoHeight: 24,
  headerTitle: 'Kompakt',
  slogan: 'Keine komplizierte Fachsprache. Keine versteckten Kosten.',
  websiteUrl: 'https://klartech-mueritz.de',
};

// Get branding config from localStorage
export function getBrandingConfig(): PdfBrandingConfig {
  try {
    const stored = localStorage.getItem('kompakt_pdf_branding');
    if (stored) {
      return { ...defaultBrandingConfig, ...JSON.parse(stored) };
    }
  } catch {
    // ignore
  }
  return defaultBrandingConfig;
}

// Save branding config to localStorage
export function saveBrandingConfig(config: PdfBrandingConfig): void {
  localStorage.setItem('kompakt_pdf_branding', JSON.stringify(config));
}

interface AnalysisData {
  fileName?: string;
  summary?: string;
  keywords?: string[];
  sentiment?: {
    overall: string;
    positive: number;
    neutral: number;
    negative: number;
  };
  themes?: Array<{ theme: string; sentiment: string }>;
  language?: string;
  createdAt?: string | Date;
}

interface ComparisonData {
  fileName1?: string;
  fileName2?: string;
  summary?: string;
  similarities?: string[];
  differences?: string[];
  uniqueDoc1?: string[];
  uniqueDoc2?: string[];
}

interface QAData {
  question: string;
  answer: string;
  fileName?: string;
}

// Helper to load image as base64 + read its intrinsic size
async function loadImageForPdf(url: string): Promise<{ dataUrl: string; width: number; height: number } | null> {
  try {
    const response = await fetch(url);
    const blob = await response.blob();

    const dataUrl = await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });

    if (!dataUrl) return null;

    const size = await new Promise<{ width: number; height: number } | null>((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth || img.width, height: img.naturalHeight || img.height });
      img.onerror = () => resolve(null);
      img.src = dataUrl;
    });

    if (!size) return null;

    return { dataUrl, width: size.width, height: size.height };
  } catch {
    return null;
  }
}

// Format date in German
function formatDateDE(date: Date): string {
  return new Intl.DateTimeFormat('de-DE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

// Professional PDF builder with compact spacing
class PdfBuilder {
  private doc: jsPDF;
  private y: number;
  private margin = 18;
  private pageWidth: number;
  private pageHeight: number;
  private contentWidth: number;
  private lineHeight = 4.5; // Compact line height

  constructor(doc: jsPDF, startY: number) {
    this.doc = doc;
    this.y = startY;
    this.pageWidth = doc.internal.pageSize.getWidth();
    this.pageHeight = doc.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - 2 * this.margin;
  }

  private checkPageBreak(neededHeight: number) {
    if (this.y + neededHeight > this.pageHeight - 25) {
      this.doc.addPage();
      this.y = this.margin;
    }
  }

  addSectionHeader(text: string) {
    this.checkPageBreak(12);
    this.doc.setFontSize(13);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(8, 145, 178); // Brand color
    this.doc.text(text, this.margin, this.y);
    this.y += 6;
    this.doc.setTextColor(0, 0, 0);
  }

  addLabelValue(label: string, value: string) {
    this.checkPageBreak(6);
    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(label, this.margin, this.y);
    const labelWidth = this.doc.getTextWidth(label) + 2;
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(value, this.margin + labelWidth, this.y);
    this.y += this.lineHeight;
  }

  addParagraph(text: string, fontSize = 10) {
    this.doc.setFontSize(fontSize);
    this.doc.setFont('helvetica', 'normal');
    const lines = this.doc.splitTextToSize(text, this.contentWidth);
    const neededHeight = lines.length * this.lineHeight;
    this.checkPageBreak(neededHeight);
    this.doc.text(lines, this.margin, this.y);
    this.y += neededHeight + 1;
  }

  addBulletList(items: string[], fontSize = 10) {
    this.doc.setFontSize(fontSize);
    this.doc.setFont('helvetica', 'normal');
    const bulletIndent = 4;
    
    for (const item of items) {
      const lines = this.doc.splitTextToSize(item, this.contentWidth - bulletIndent);
      const neededHeight = lines.length * this.lineHeight;
      this.checkPageBreak(neededHeight);
      
      this.doc.text('•', this.margin, this.y);
      this.doc.text(lines, this.margin + bulletIndent, this.y);
      this.y += neededHeight;
    }
  }

  addSentimentBar(positive: number, neutral: number, negative: number) {
    this.checkPageBreak(14);
    const barY = this.y;
    const barHeight = 6;
    const barWidth = this.contentWidth - 60;
    const startX = this.margin + 60;
    
    // Labels
    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'normal');
    
    // Draw stacked bar
    let currentX = startX;
    
    // Positive (green)
    if (positive > 0) {
      const w = (positive / 100) * barWidth;
      this.doc.setFillColor(34, 197, 94);
      this.doc.rect(currentX, barY, w, barHeight, 'F');
      currentX += w;
    }
    
    // Neutral (gray)
    if (neutral > 0) {
      const w = (neutral / 100) * barWidth;
      this.doc.setFillColor(156, 163, 175);
      this.doc.rect(currentX, barY, w, barHeight, 'F');
      currentX += w;
    }
    
    // Negative (red)
    if (negative > 0) {
      const w = (negative / 100) * barWidth;
      this.doc.setFillColor(239, 68, 68);
      this.doc.rect(currentX, barY, w, barHeight, 'F');
    }
    
    // Legend below bar
    this.y = barY + barHeight + 3;
    this.doc.setFontSize(8);
    
    let legendX = startX;
    this.doc.setFillColor(34, 197, 94);
    this.doc.rect(legendX, this.y, 3, 3, 'F');
    this.doc.text(`Positiv ${positive}%`, legendX + 4, this.y + 2.5);
    
    legendX += 35;
    this.doc.setFillColor(156, 163, 175);
    this.doc.rect(legendX, this.y, 3, 3, 'F');
    this.doc.text(`Neutral ${neutral}%`, legendX + 4, this.y + 2.5);
    
    legendX += 35;
    this.doc.setFillColor(239, 68, 68);
    this.doc.rect(legendX, this.y, 3, 3, 'F');
    this.doc.text(`Negativ ${negative}%`, legendX + 4, this.y + 2.5);
    
    this.y += 6;
  }

  addSpace(mm: number) {
    this.y += mm;
  }

  getY() {
    return this.y;
  }
}

// Create branded PDF header
async function createBrandedPDF(title: string, config: PdfBrandingConfig) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const headerHeight = 38;

  // Header background
  doc.setFillColor(8, 145, 178);
  doc.rect(0, 0, pageWidth, headerHeight, 'F');

  // Load logo
  const logoUrl = '/images/logo-klartech.png';
  const logo = await loadImageForPdf(logoUrl);

  doc.setTextColor(255, 255, 255);

  if (logo) {
    const logoX = 14;
    const logoY = 7;
    const targetLogoH = config.logoHeight;
    const ratio = logo.width / logo.height;
    const targetLogoW = targetLogoH * ratio;

    doc.addImage(logo.dataUrl, 'PNG', logoX, logoY, targetLogoW, targetLogoH);

    // Header title next to logo
    const textX = logoX + targetLogoW + 6;
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text(config.headerTitle, textX, 20);

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(title, textX, 28);
  } else {
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text(config.headerTitle, 18, 20);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(title, 18, 28);
  }

  doc.setTextColor(0, 0, 0);

  return doc;
}

// Add footer to PDF
function addFooter(doc: jsPDF, config: PdfBrandingConfig) {
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();
  const footerY = pageHeight - 12;

  // Separator line
  doc.setDrawColor(200, 200, 200);
  doc.line(18, footerY - 3, pageWidth - 18, footerY - 3);

  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text(`Klartech Müritz – ${config.slogan}`, pageWidth / 2, footerY, { align: 'center' });
  doc.text(config.websiteUrl, pageWidth / 2, footerY + 4, { align: 'center' });
}

// Helper to add single analysis content to builder
function addAnalysisContent(builder: PdfBuilder, analysis: AnalysisData, includeHeader = true) {
  if (includeHeader) {
    builder.addSectionHeader('Dokument-Information');
  }
  if (analysis.fileName) {
    builder.addLabelValue('Dateiname:', analysis.fileName);
  }
  const date = analysis.createdAt ? new Date(analysis.createdAt) : new Date();
  builder.addLabelValue('Analysiert:', formatDateDE(date));
  builder.addSpace(4);

  // Summary
  if (analysis.summary) {
    builder.addSectionHeader('Zusammenfassung');
    builder.addParagraph(analysis.summary);
    builder.addSpace(3);
  }

  // Keywords
  if (analysis.keywords && analysis.keywords.length > 0) {
    builder.addSectionHeader('Keywords');
    builder.addParagraph(analysis.keywords.join('  •  '));
    builder.addSpace(3);
  }

  // Sentiment Analysis
  if (analysis.sentiment) {
    builder.addSectionHeader('Sentiment-Analyse');
    builder.addLabelValue('Gesamtbewertung:', analysis.sentiment.overall);
    builder.addSpace(2);
    builder.addSentimentBar(
      analysis.sentiment.positive,
      analysis.sentiment.neutral,
      analysis.sentiment.negative
    );
    builder.addSpace(3);
  }

  // Themes
  if (analysis.themes && analysis.themes.length > 0) {
    builder.addSectionHeader('Hauptthemen');
    builder.addBulletList(
      analysis.themes.map(t => `${t.theme} (${t.sentiment})`)
    );
  }
}

export async function exportAnalysisToPDF(analysis: AnalysisData) {
  const config = getBrandingConfig();
  const doc = await createBrandedPDF('Dokumentenanalyse', config);
  const builder = new PdfBuilder(doc, 46);

  addAnalysisContent(builder, analysis);

  addFooter(doc, config);

  const fileName = analysis.fileName
    ? `${analysis.fileName.replace(/\.[^/.]+$/, '')}_Analyse.pdf`
    : `Analyse_${new Date().toISOString().split('T')[0]}.pdf`;

  doc.save(fileName);
}

// Export multiple analyses into a single combined PDF
export async function exportBatchAnalysesToPDF(analyses: AnalysisData[]) {
  if (analyses.length === 0) return;

  const config = getBrandingConfig();
  const doc = await createBrandedPDF(`Batch-Analyse (${analyses.length} Dokumente)`, config);
  
  // Table of contents
  let y = 46;
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(8, 145, 178);
  doc.text('Inhaltsverzeichnis', 18, y);
  y += 8;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  analyses.forEach((a, idx) => {
    doc.text(`${idx + 1}. ${a.fileName || `Dokument ${idx + 1}`}`, 22, y);
    y += 5;
  });
  
  // Add each analysis on new page
  for (let i = 0; i < analyses.length; i++) {
    doc.addPage();
    
    // Document header
    doc.setFillColor(240, 240, 240);
    doc.rect(0, 0, doc.internal.pageSize.getWidth(), 14, 'F');
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(8, 145, 178);
    doc.text(`Dokument ${i + 1} von ${analyses.length}`, 18, 10);
    doc.setTextColor(0, 0, 0);
    
    const builder = new PdfBuilder(doc, 22);
    addAnalysisContent(builder, analyses[i]);
  }

  addFooter(doc, config);
  doc.save(`Batch_Analyse_${new Date().toISOString().split('T')[0]}.pdf`);
}

export async function exportComparisonToPDF(comparison: ComparisonData) {
  const config = getBrandingConfig();
  const doc = await createBrandedPDF('Dokumenten-Vergleich', config);
  const builder = new PdfBuilder(doc, 46);

  // Info
  builder.addSectionHeader('Vergleichsinformation');
  if (comparison.fileName1) {
    builder.addLabelValue('Dokument 1:', comparison.fileName1);
  }
  if (comparison.fileName2) {
    builder.addLabelValue('Dokument 2:', comparison.fileName2);
  }
  builder.addLabelValue('Erstellt:', formatDateDE(new Date()));
  builder.addSpace(4);

  // Summary
  if (comparison.summary) {
    builder.addSectionHeader('Zusammenfassung');
    builder.addParagraph(comparison.summary);
    builder.addSpace(3);
  }

  // Similarities
  if (comparison.similarities && comparison.similarities.length > 0) {
    builder.addSectionHeader('Gemeinsamkeiten');
    builder.addBulletList(comparison.similarities);
    builder.addSpace(3);
  }

  // Differences
  if (comparison.differences && comparison.differences.length > 0) {
    builder.addSectionHeader('Unterschiede');
    builder.addBulletList(comparison.differences);
    builder.addSpace(3);
  }

  // Unique to Doc 1
  if (comparison.uniqueDoc1 && comparison.uniqueDoc1.length > 0) {
    builder.addSectionHeader(`Nur in ${comparison.fileName1 || 'Dokument 1'}`);
    builder.addBulletList(comparison.uniqueDoc1);
    builder.addSpace(3);
  }

  // Unique to Doc 2
  if (comparison.uniqueDoc2 && comparison.uniqueDoc2.length > 0) {
    builder.addSectionHeader(`Nur in ${comparison.fileName2 || 'Dokument 2'}`);
    builder.addBulletList(comparison.uniqueDoc2);
  }

  addFooter(doc, config);
  doc.save(`Vergleich_${new Date().toISOString().split('T')[0]}.pdf`);
}

export async function exportQAToPDF(qa: QAData) {
  const config = getBrandingConfig();
  const doc = await createBrandedPDF('Frage & Antwort', config);
  const builder = new PdfBuilder(doc, 46);

  // Info
  builder.addLabelValue('Erstellt:', formatDateDE(new Date()));
  if (qa.fileName) {
    builder.addLabelValue('Dokument:', qa.fileName);
  }
  builder.addSpace(4);

  // Question
  builder.addSectionHeader('Frage');
  builder.addParagraph(qa.question);
  builder.addSpace(3);

  // Answer
  builder.addSectionHeader('Antwort');
  builder.addParagraph(qa.answer);

  addFooter(doc, config);
  doc.save(`QA_${new Date().toISOString().split('T')[0]}.pdf`);
}

// Copy to clipboard helper
export function copyToClipboard(text: string): Promise<void> {
  return navigator.clipboard.writeText(text);
}

// Format analysis for text export
export function formatAnalysisAsText(analysis: AnalysisData): string {
  let text = '=== KLARTECH KOMPAKT - ANALYSE ===\n\n';

  if (analysis.fileName) {
    text += `Dateiname: ${analysis.fileName}\n`;
  }
  text += `Datum: ${new Date().toLocaleString('de-DE')}\n\n`;

  if (analysis.summary) {
    text += `--- ZUSAMMENFASSUNG ---\n${analysis.summary}\n\n`;
  }

  if (analysis.keywords?.length) {
    text += `--- KEYWORDS ---\n${analysis.keywords.join(', ')}\n\n`;
  }

  if (analysis.sentiment) {
    text += `--- SENTIMENT ---\n`;
    text += `Gesamt: ${analysis.sentiment.overall}\n`;
    text += `Positiv: ${analysis.sentiment.positive}%\n`;
    text += `Neutral: ${analysis.sentiment.neutral}%\n`;
    text += `Negativ: ${analysis.sentiment.negative}%\n\n`;
  }

  if (analysis.themes?.length) {
    text += `--- THEMEN ---\n`;
    analysis.themes.forEach((t) => {
      text += `• ${t.theme} (${t.sentiment})\n`;
    });
  }

  return text;
}

// Format comparison for text export
export function formatComparisonAsText(comparison: ComparisonData): string {
  let text = '=== KLARTECH KOMPAKT - VERGLEICH ===\n\n';

  text += `Dokument 1: ${comparison.fileName1 || 'Unbenannt'}\n`;
  text += `Dokument 2: ${comparison.fileName2 || 'Unbenannt'}\n`;
  text += `Datum: ${new Date().toLocaleString('de-DE')}\n\n`;

  if (comparison.summary) {
    text += `--- ZUSAMMENFASSUNG ---\n${comparison.summary}\n\n`;
  }

  if (comparison.similarities?.length) {
    text += `--- GEMEINSAMKEITEN ---\n`;
    comparison.similarities.forEach((s) => (text += `• ${s}\n`));
    text += '\n';
  }

  if (comparison.differences?.length) {
    text += `--- UNTERSCHIEDE ---\n`;
    comparison.differences.forEach((d) => (text += `• ${d}\n`));
    text += '\n';
  }

  return text;
}
